import { useEffect, useId } from "react";
import { getOptionAxes, sortOptionValues, valueState, type CheckoutProduct, type OptionSelection, type OptionValueImage } from "@/lib/express-cart/variants";

interface Props {
  product: CheckoutProduct;
  selection: OptionSelection;
  onSelect: (axisName: string, value: string) => void;
}

export function VariantPicker({ product, selection, onSelect }: Props) {
  // check to see if there is only one variant in stock
  const hasStock: Array<Record<string, string>> = []
  let axisLoopFinished = false
  useEffect(() => {
    if (axisLoopFinished && hasStock.length == 1){
      const [entry] = Object.entries(hasStock[0] ?? {})
      if (entry) onSelect(entry[0], entry[1])
    }
  }, [axisLoopFinished])
  // Unique per row: a product can have several rows, each with its own option groups.
  const idPrefix = useId();
  const axes = getOptionAxes(product);
  if (axes.length === 0) return null;
  

  return (
    <div className="space-y-3">
      {axes.map((axis) => {
        const labelId = `${idPrefix}-${axis.name}-label`;
        const isScrollable = axis.values.length > 4;
        
        return (
          <div key={axis.name}>
            <p id={labelId} className="mb-1.5 text-xs font-medium text-(--color-text-secondary)">
              {axis.name}
              {/* {selection[axis.name] ? `: ${selection[axis.name]}` : ""} */}
            </p>
            <div
              role="radiogroup"
              aria-labelledby={labelId}
              className={isScrollable
                ? `flex max-h-80 ${axis.hasImages ? "flex-col flex-nowrap" : "flex-wrap"} gap-2 overflow-y-auto pr-1`
                : "flex flex-wrap gap-2"}
            >
              {sortOptionValues(axis.values.map((option) => typeof option === "string" ? option : option.value)).map((value, index) => {
                const option = axis.values.find((candidate) => (typeof candidate === "string" ? candidate : candidate.value) === value);
                const imageUrl = axis.hasImages && option && typeof option !== "string"
                  ? (option as OptionValueImage).image_url
                  : undefined;
                const state = valueState(product, selection, axis.name, value);
                const selected = selection[axis.name] === value;
                const disabled = state === "sold-out";
                const suffix =
                  state === "sold-out" ? " (sold out)" : state === "unavailable" ? " (not available in this combination)" : "";

                if (!disabled) hasStock.push({[axis.name]: value});

                if (axis.values.length == (index+1)) axisLoopFinished = true; // end of loop and values

                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`${value}${suffix}`}
                    disabled={disabled}
                    onClick={() => onSelect(axis.name, value)}
                    className={[
                      isScrollable && axis.hasImages
                        ? "w-full rounded-lg border px-3 py-1.5 text-left text-sm transition"
                        : "min-w-11 rounded-lg border px-3 py-1.5 text-sm transition",
                      selected
                        ? "border-(--color-primary) bg-(--color-primary) text-(--color-secondary)"
                        : "border-(--color-surface-border) bg-(--color-surface-primary) hover:border-(--color-primary)",
                      disabled ? "cursor-not-allowed border-dashed text-(--color-text-placeholder) line-through hover:border-(--color-surface-border)" : "",
                      state === "unavailable" && !selected ? "text-(--color-text-placeholder)" : "",
                    ].join(" ")}
                  >
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => { event.currentTarget.hidden = true; }}
                        className="mr-2 inline-block h-16 w-16 rounded-md object-cover align-middle"
                      />
                    )}
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
